/**
 * AudioMixer — Web Audio API–based mixer for multi-call conferencing.
 *
 * Creates per-session sub-mixes so each remote party hears all OTHER
 * parties plus the local microphone, but never their own audio echoed back.
 *
 * Audio routing architecture:
 *   For session A: mix = local mic + B audio + C audio (excludes A)
 *   For session B: mix = local mic + A audio + C audio (excludes B)
 *   Local output (user's speaker): A audio + B audio + C audio (all remotes)
 *
 * Each per-session output stream is intended to be sent to that session's
 * RTCRtpSender via replaceTrack().
 */

// ---------- Interfaces ----------

export interface AudioMixerOptions {
  sampleRate?: number; // Default: browser default (typically 48000)
}

export interface MixedParticipant {
  sessionId: string;
  sourceNode: MediaStreamAudioSourceNode;
  gainNode: GainNode; // Per-participant gain control
}

/**
 * Internal structure tracking the per-session sub-mix destination.
 * Each session gets its own MediaStreamAudioDestinationNode that
 * receives all OTHER participants' audio + local mic.
 */
interface SessionSubMix {
  destination: MediaStreamAudioDestinationNode;
}

// ---------- Class ----------

export class AudioMixer {
  private context: AudioContext | null = null;
  private participants: Map<string, MixedParticipant> = new Map();
  private localMicSource: MediaStreamAudioSourceNode | null = null;
  private localMicGain: GainNode | null = null;
  private localMicStream: MediaStream | null = null;

  /** Local output destination — mixes all remote participants for the user's speaker */
  private outputDestination: MediaStreamAudioDestinationNode | null = null;
  private outputStream: MediaStream | null = null;

  /** Per-session sub-mix destinations (each excludes that session's own audio) */
  private sessionSubMixes: Map<string, SessionSubMix> = new Map();

  // ---------- Lifecycle ----------

  /**
   * Create the AudioContext and output destination node.
   * Must be called before adding participants.
   */
  create(options?: AudioMixerOptions): void {
    if (this.context) {
      // Already created — no-op
      return;
    }

    const contextOptions: AudioContextOptions = {};
    if (options?.sampleRate) {
      contextOptions.sampleRate = options.sampleRate;
    }

    this.context = new AudioContext(contextOptions);

    // Create the local output destination (all remotes mixed for user's speaker)
    this.outputDestination = this.context.createMediaStreamDestination();
    this.outputStream = this.outputDestination.stream;
  }

  /**
   * Tear down the AudioContext and disconnect all nodes.
   * Releases all resources.
   */
  destroy(): void {
    if (!this.context) return;

    // Disconnect all participants
    const participantIds = Array.from(this.participants.keys());
    participantIds.forEach((sessionId) => {
      this.disconnectParticipant(sessionId);
    });
    this.participants.clear();

    // Disconnect local mic
    if (this.localMicSource) {
      try {
        this.localMicSource.disconnect();
      } catch {
        // Already disconnected
      }
      this.localMicSource = null;
    }
    if (this.localMicGain) {
      try {
        this.localMicGain.disconnect();
      } catch {
        // Already disconnected
      }
      this.localMicGain = null;
    }
    this.localMicStream = null;

    // Clean up sub-mix destinations
    this.sessionSubMixes.clear();

    // Close the audio context
    this.outputDestination = null;
    this.outputStream = null;

    try {
      this.context.close();
    } catch {
      // Context already closed or closing
    }
    this.context = null;
  }

  /**
   * Returns true if the AudioContext is created and active.
   */
  isActive(): boolean {
    return this.context !== null && this.context.state !== "closed";
  }

  // ---------- Participant management ----------

  /**
   * Add a remote participant's audio stream to the mixer.
   * Creates a MediaStreamAudioSourceNode → GainNode chain,
   * connects to the local output destination, and rebuilds
   * per-session sub-mixes.
   */
  addParticipant(sessionId: string, remoteStream: MediaStream): void {
    if (!this.context || !this.outputDestination) {
      throw new Error("AudioMixer not initialized. Call create() first.");
    }

    // If participant already exists, remove and re-add
    if (this.participants.has(sessionId)) {
      this.removeParticipant(sessionId);
    }

    // Create source node from the remote stream
    const sourceNode = this.context.createMediaStreamSource(remoteStream);

    // Create per-participant gain node for volume control
    const gainNode = this.context.createGain();
    gainNode.gain.value = 1.0;

    // Chain: sourceNode → gainNode
    sourceNode.connect(gainNode);

    // Connect to local output (user hears all remotes)
    gainNode.connect(this.outputDestination);

    const participant: MixedParticipant = {
      sessionId,
      sourceNode,
      gainNode,
    };

    this.participants.set(sessionId, participant);

    // Rebuild all per-session sub-mixes to include the new participant
    this.rebuildSubMixes();
  }

  /**
   * Remove a participant from the mixer.
   * Disconnects their audio nodes and rebuilds sub-mixes.
   */
  removeParticipant(sessionId: string): void {
    this.disconnectParticipant(sessionId);
    this.participants.delete(sessionId);

    // Remove its sub-mix destination
    this.sessionSubMixes.delete(sessionId);

    // Rebuild remaining sub-mixes
    this.rebuildSubMixes();
  }

  /**
   * Get the number of participants currently in the mixer.
   */
  getParticipantCount(): number {
    return this.participants.size;
  }

  /**
   * Get an array of all participant session IDs.
   */
  getParticipantIds(): string[] {
    return Array.from(this.participants.keys());
  }

  // ---------- Local mic routing ----------

  /**
   * Set or update the local microphone stream.
   * Routes it to all per-session sub-mixes so each remote party
   * hears the local user.
   */
  setLocalMicStream(stream: MediaStream): void {
    if (!this.context) {
      throw new Error("AudioMixer not initialized. Call create() first.");
    }

    // Disconnect previous mic source if exists
    if (this.localMicSource) {
      try {
        this.localMicSource.disconnect();
      } catch {
        // Already disconnected
      }
    }
    if (this.localMicGain) {
      try {
        this.localMicGain.disconnect();
      } catch {
        // Already disconnected
      }
    }

    this.localMicStream = stream;

    // Create source and gain for the local mic
    this.localMicSource = this.context.createMediaStreamSource(stream);
    this.localMicGain = this.context.createGain();
    this.localMicGain.gain.value = 1.0;

    this.localMicSource.connect(this.localMicGain);

    // Connect mic to all existing per-session sub-mixes
    this.connectMicToSubMixes();
  }

  // ---------- Output ----------

  /**
   * Get the output MediaStream for the local user's speaker.
   * Contains all remote participants' audio mixed together.
   */
  getOutputStream(): MediaStream | null {
    return this.outputStream;
  }

  // ---------- Per-session mixed output ----------

  /**
   * Get a MediaStream for a specific session that contains all OTHER
   * participants' audio + local mic, excluding that session's own audio.
   *
   * This stream is intended to be sent to that session's RTCRtpSender
   * via replaceTrack().
   */
  getMixedStreamForSession(sessionId: string): MediaStream {
    if (!this.context) {
      throw new Error("AudioMixer not initialized. Call create() first.");
    }

    const subMix = this.sessionSubMixes.get(sessionId);
    if (subMix) {
      return subMix.destination.stream;
    }

    // If no sub-mix exists yet (session not yet known), build it
    this.buildSubMixForSession(sessionId);
    const newSubMix = this.sessionSubMixes.get(sessionId);
    if (newSubMix) {
      return newSubMix.destination.stream;
    }

    // Fallback: return an empty MediaStream
    return new MediaStream();
  }

  // ---------- Private helpers ----------

  /**
   * Disconnect a participant's audio nodes from the graph.
   */
  private disconnectParticipant(sessionId: string): void {
    const participant = this.participants.get(sessionId);
    if (!participant) return;

    try {
      participant.gainNode.disconnect();
    } catch {
      // Already disconnected
    }
    try {
      participant.sourceNode.disconnect();
    } catch {
      // Already disconnected
    }
  }

  /**
   * Rebuild all per-session sub-mix destinations.
   *
   * Each session's sub-mix includes:
   *   - All OTHER participants' gain nodes
   *   - The local mic gain node (if set)
   * But excludes that session's own gain node.
   */
  private rebuildSubMixes(): void {
    if (!this.context) return;

    // Disconnect existing sub-mix connections (gain nodes → sub-mix destinations)
    // We'll rebuild all connections from scratch
    this.sessionSubMixes.forEach((subMix) => {
      try {
        subMix.destination.disconnect();
      } catch {
        // ignore
      }
    });
    this.sessionSubMixes.clear();

    // Rebuild each session's sub-mix
    const sessionIds = Array.from(this.participants.keys());
    sessionIds.forEach((sessionId) => {
      this.buildSubMixForSession(sessionId);
    });
  }

  /**
   * Build a per-session sub-mix destination for one session.
   * Connects all OTHER participants' gain nodes + local mic to this destination.
   */
  private buildSubMixForSession(sessionId: string): void {
    if (!this.context) return;

    // Create a new destination for this session's sub-mix
    const destination = this.context.createMediaStreamDestination();

    // Connect all OTHER participants' gain nodes to this destination
    this.participants.forEach((otherParticipant, otherSessionId) => {
      if (otherSessionId === sessionId) return; // Exclude self
      try {
        otherParticipant.gainNode.connect(destination);
      } catch {
        // Node may be in an invalid state
      }
    });

    // Connect local mic gain to this session's sub-mix (if set)
    if (this.localMicGain) {
      try {
        this.localMicGain.connect(destination);
      } catch {
        // ignore
      }
    }

    this.sessionSubMixes.set(sessionId, { destination });
  }

  /**
   * Connect the local mic gain node to all existing per-session sub-mix destinations.
   */
  private connectMicToSubMixes(): void {
    if (!this.localMicGain) return;

    this.sessionSubMixes.forEach((subMix) => {
      try {
        this.localMicGain!.connect(subMix.destination);
      } catch {
        // ignore
      }
    });
  }
}
